import { useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiCalendar, FiCheck, FiChevronLeft, FiShoppingBag } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { AdminContext } from '../../Context/AdminContext';
import { apiRequest } from '../../Services/api';
import { CartLineItem, OrderSummary, formatCurrency } from '../ProductsPage/StorefrontComponents';
import { formatEasternDate } from '../../Utils/dateTime';
import './Cart.css';

function formatDate(value) {
  if (!value) return '';
  return formatEasternDate(value, { month: 'long', day: 'numeric', year: 'numeric' });
}

export default function Cart() {
  const { user, items, setItems, updateItem, removeItems, cartBusy } = useContext(AdminContext);
  const [policy, setPolicy] = useState({ pickupLeadDays: 21, minPickupDate: '' });
  const [pickupDate, setPickupDate] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const subtotal = useMemo(() => items.reduce((total, item) => total + Number(item.lineTotal || 0), 0), [items]);

  useEffect(() => {
    let active = true;
    apiRequest('/products/index.php').then((data) => {
      if (!active || !data.orderingPolicy) return;
      setPolicy(data.orderingPolicy);
      setPickupDate(data.orderingPolicy.minPickupDate);
    }).catch((error) => toast.error(error.message));
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const priorTitle = document.title;
    const robots = document.head.querySelector('meta[name="robots"]');
    const canonical = document.head.querySelector('link[rel="canonical"]');
    const priorRobots = robots?.getAttribute('content');
    const priorCanonical = canonical?.getAttribute('href');
    document.title = "Your Pickup Order | Aunt Jack's Peanut Shack";
    robots?.setAttribute('content', 'noindex,nofollow');
    canonical?.setAttribute('href', 'https://www.auntjackspeanutshack.com/cart/');
    return () => {
      document.title = priorTitle;
      if (priorRobots) robots?.setAttribute('content', priorRobots);
      if (priorCanonical) canonical?.setAttribute('href', priorCanonical);
    };
  }, []);

  const placeOrder = async (event) => {
    event.preventDefault();
    if (!pickupDate || pickupDate < policy.minPickupDate) {
      toast.error(`Choose a pickup date on or after ${formatDate(policy.minPickupDate)}.`);
      return;
    }
    setIsSubmitting(true);
    try {
      const data = await apiRequest('/orders/index.php', { method: 'POST', body: JSON.stringify({ pickupDate }) });
      setItems([]);
      setConfirmation(data.order);
    } catch (error) {
      toast.error(error.message || 'Unable to place your order.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCartUpdate = async (id, quantity) => {
    try { await updateItem(id, quantity); } catch (error) { toast.error(error.message || 'Unable to update the cart.'); }
  };

  const handleCartRemove = async (id) => {
    try { await removeItems(id); } catch (error) { toast.error(error.message || 'Unable to remove that item.'); }
  };

  if (confirmation) {
    return (
      <main className='checkout-page checkout-confirmation'>
        <div className='confirmation-mark'><FiCheck aria-hidden='true' /></div>
        <h1>Your pickup order is in.</h1>
        <p>Thanks, {user?.displayName}. Jackie has received order <strong>#{confirmation.id}</strong>.</p>
        <dl>
          <div><dt>Pickup date</dt><dd>{formatDate(confirmation.pickupDate)}</dd></div>
          <div><dt>Order total</dt><dd>{formatCurrency(confirmation.totalAmount)}</dd></div>
          <div><dt>Status</dt><dd>{confirmation.status}</dd></div>
        </dl>
        <div className='confirmation-actions'><Link to='/shop'>Continue shopping</Link><Link to='/profile'>View my orders</Link></div>
      </main>
    );
  }

  return (
    <main className='checkout-page'>
      <div className='checkout-heading'>
        <Link to='/shop'><FiChevronLeft aria-hidden='true' /> Continue shopping</Link>
        <h1>Your pickup order</h1>
        <p>Review your favorites and choose the date you’d like them ready.</p>
      </div>

      {!items.length ? (
        <section className='checkout-empty'>
          <FiShoppingBag aria-hidden='true' /><h2>Your cart is empty.</h2><p>There’s always room for a warm bag of boiled peanuts.</p><Link to='/shop'>Shop the boil</Link>
        </section>
      ) : (
        <form className='checkout-layout' onSubmit={placeOrder}>
          <section className='checkout-items' aria-labelledby='checkout-items-title'>
            <h2 id='checkout-items-title'>Your selections</h2>
            {items.map((item) => <CartLineItem item={item} onUpdate={handleCartUpdate} onRemove={handleCartRemove} busy={cartBusy} key={item.id} />)}
          </section>
          <aside className='checkout-sidebar'>
            <section className='checkout-customer'>
              <h2>Pickup contact</h2><strong>{user?.displayName}</strong><span>{user?.email}</span><span>{user?.phone}</span>
            </section>
            <section className='checkout-date'>
              <h2><FiCalendar aria-hidden='true' /> Choose a pickup date</h2>
              <p>Orders need at least {policy.pickupLeadDays} days to prepare.</p>
              <label htmlFor='pickup-date'>Pickup date</label>
              <input id='pickup-date' type='date' value={pickupDate} min={policy.minPickupDate} onChange={(event) => setPickupDate(event.target.value)} required />
              {policy.minPickupDate ? <small>Earliest available: {formatDate(policy.minPickupDate)}</small> : null}
            </section>
            <OrderSummary items={items} policy={policy}>
              <button className='checkout-submit' type='submit' disabled={isSubmitting || cartBusy || !pickupDate}>
                {isSubmitting ? 'Placing your order…' : `Place pickup order — ${formatCurrency(subtotal)}`}
              </button>
              <p className='checkout-disclaimer'>No payment is collected online. This submits your pickup order to Aunt Jack’s.</p>
            </OrderSummary>
          </aside>
        </form>
      )}
    </main>
  );
}
